import React from 'react';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Alert, Button, Modal, Select, InputNumber, message, Switch } from 'antd';
import { connect } from 'react-redux';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { withRouter } from 'react-router';
import {
	getCurrency,
	getCustomFieldsForAPI,
	getObjectValues,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import BackButton from '../back_button/BackButton';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { getProtectedImageUriForSupplier } from '../../utils/FileAccessUtils';
import { imageUploadValidation, normFile } from '../../utils/ImageUtils';
import { partsRestCrudThunksForSupplier } from '../../thunks/parts_thunks';
import { unitsOfMeasureRestCrudThunksForSupplier } from '../../thunks/units_of_measure_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { OWButton } from '../ow_button/OWButton';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import { replacePartVendorsForSupplier } from '../../thunks/part_catalogs_thunks';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import { PERMISSION_NAMES, isSupplierAllowedToAccess } from '../../utils/AuthUtils';
import OWUpload from '../OWUpload';
import ClearErrors from '../clear_errors/ClearErrors';
import { PARTS_CRUD_ACTION_CREATORS } from '../../actions/parts_actions';
import { getCustomFieldsForSupplier } from '../../thunks/custom_fields_thunks';
import CustomFieldItems from '../custom_fields_items/CustomFieldItems';
import { isGLCodeAllowedBySettings } from '../../utils/InventoryUtils';

require('./PartForm.less');

interface PartFormProps extends FormComponentProps {
	creating: boolean;
	updating: boolean;
	formData: any;
	history: any;
	redirectForwardUrl?: string;
	onSuccess?: any;
	createErrors: any[];
	updateErrors: any[];
	currentUser: any;
	create: any;
	update: any;
	replaceVendors: any;
	unitsOfMeasure: any;
	fetchUnitsOfMeasure: any;
	fetchVendors: any;
	fetchMultipleVendors: any;
	vendors: any;
	createVendor: any;
	onCancel?: any;
	companyConfig: any;
	getCustomFields: any;
}

class PartForm extends React.Component<PartFormProps, any> {
	state = {
		previewVisible: false,
		uploadLoading: false,
		previewImage: null,
		allPhotos: [],
		submitting: false,
		customFields: [],
	};

	componentDidMount() {
		const { fetchUnitsOfMeasure, formData, getCustomFields } = this.props;
		fetchUnitsOfMeasure({});
		getCustomFields({ onEntity: 'part' }).then((data) => {
			this.setState({ customFields: data });
		});

		let photosInfo;
		if (formData.images) {
			photosInfo = formData.images.map((p, idx) => {
				const [imgFileId, imgFileName] = p.split('/');
				const thumbUri = getProtectedImageUriForSupplier(imgFileId, imgFileName, 100, 100);
				const fullUri = getProtectedImageUriForSupplier(imgFileId, imgFileName, 400, 400);
				return {
					uid: idx,
					size: 1,
					name: imgFileName,
					filename: imgFileName,
					url: fullUri,
					status: 'done',
					thumbUrl: thumbUri,
					response: { data: { fileName: imgFileName, fileId: imgFileId } },
					type: 'image/jpeg',
				};
			});
		} else {
			photosInfo = [];
		}
		this.setState({ allPhotos: photosInfo });
	}

	onSubmitSuccess = (record) => {
		const { history, onSuccess, redirectForwardUrl } = this.props;
		if (redirectForwardUrl) {
			history.push(redirectForwardUrl);
		}
		if (onSuccess) {
			onSuccess(record);
		}
	};

	handleSubmit = (e, isUpdate) => {
		const { formData, replaceVendors, update, create } = this.props;
		e.preventDefault();
		this.props.form.validateFields((err, values) => {
			if (!err) {
				this.setState({ submitting: true });

				const submitAction = isUpdate ? update : create;

				const picturesInfo = values.images.map((p) => {
					const fileName = nullSafeGetOrElse('response.data.fileName', p, '');
					const fileId = nullSafeGetOrElse('response.data.fileId', p, '');
					return `${fileId}/${fileName}`;
				});

				const allVendorIds = Array.from(
					new Set([
						...nullSafeGetOrElse('partEquipmentVendorIds', values, []),
						...(values.defaultVendorId ? [values.defaultVendorId] : []),
					])
				);

				const partCatalogs = allVendorIds.map((vendorId) => {
					return {
						partEquipmentVendorId: vendorId,
						currencyId: 'USD',
						supplierFacilityId: values.supplierFacilityId,
						supplierCompanyId: values.supplierCompanyId,
					};
				});
				const customFieldDetails = getCustomFieldsForAPI(values, this.state.customFields);

				const part = {
					...formData,
					id: values.id,
					isActive: values.isActive,
					name: values.name,
					description: values.description,
					images: picturesInfo,
					manufacturerId: values.manufacturerId,
					manufacturerName: values.manufacturerName,
					partNumber: values.partNumber,
					aliasPartId: values.aliasPartId,
					glCode: values.glCode,
					...(values.cost && { cost: parseFloat(values.cost) }),
					uomId: values.uomId,
					supplierCompanyId: values.supplierCompanyId,
					defaultVendorId: values.defaultVendorId || formData.defaultVendorId,
					customFieldDetails: customFieldDetails.filter(
						(field) => field.valueAsString !== undefined && field.valueAsString !== ''
					),
				};

				submitAction(part)
					.then((record) => {
						if (!isUpdate && partCatalogs.length > 0) {
							const updatedPartCatalog = partCatalogs.map((partCatalog) => {
								return {
									...partCatalog,
									partId: record.id,
								};
							});
							replaceVendors(record.id, updatedPartCatalog)
								.then((vendors) => {
									this.onSubmitSuccess(record);
								})
								.catch((err) => message.error(err))
								.finally(() => this.setState({ submitting: true }));
						} else {
							this.setState({ submitting: false });
							this.onSubmitSuccess(record);
						}
					})
					.catch((err) => {
						this.setState({ submitting: false });
						message.error(err);
					});
			}
		});
	};

	handlePreview = (file) => {
		this.setState({
			previewImage: file.url || file.thumbUrl,
			previewVisible: true,
		});
	};

	handleCancel = () => this.setState({ previewVisible: false });

	handleImageUpdate = (info) => {
		if (info.file.status === 'done') {
			this.setState({ allPhotos: info.fileList });
		} else if (info.file.status === undefined) {
			info.fileList = this.state.allPhotos;
		}
	};

	render() {
		const {
			form,
			createErrors,
			formData,
			updateErrors,
			currentUser,
			unitsOfMeasure,
			fetchVendors,
			fetchMultipleVendors,
			vendors,
			createVendor,
			onCancel,
			companyConfig,
		} = this.props;
		const { previewVisible, previewImage } = this.state;
		const { getFieldDecorator, getFieldValue } = form;
		const isUpdate = formData && formData.id !== undefined;

		const submitText = isUpdate ? 'Update part' : 'Create part';
		const currency = getCurrency({ currentUser });

		const unitsOfMeasureRecords = getObjectValues(unitsOfMeasure.records);

		const BACKEND_URI = getBackendUri();
		const uploadHeaders = { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token };

		getFieldDecorator('supplierFacilityId', {
			initialValue: nullSafeGet('facility.id', currentUser),
		});
		getFieldDecorator('supplierCompanyId', {
			initialValue:
				formData.supplierCompanyId || nullSafeGet('facility.supplierCompanyId', currentUser),
		});

		getFieldDecorator('id', { initialValue: formData.id });

		const canCreateVendors = isSupplierAllowedToAccess(
			PERMISSION_NAMES.MODIFY_VENDORS,
			companyConfig,
			currentUser.roles
		);

		const canShowCost =
			nullSafeGetOrElse('partEquipmentVendorCount', formData, 0) <= 0 &&
			nullSafeGetOrElse('length', getFieldValue('partEquipmentVendorIds'), 0) <= 0 &&
			!getFieldValue('defaultVendorId');

		const isImageUploading = (getFieldValue('images') || []).some((_) => _.status === 'uploading');

		//TODO: UPGRADE FORM
		return (
			<Form
				layout="vertical"
				onSubmit={(e) => this.handleSubmit(e, isUpdate)}
				className="supplierForm"
			>
				<ClearErrors actionCreators={PARTS_CRUD_ACTION_CREATORS} />
				{createErrors.length > 0 ? (
					<Form.Item>
						<Alert message={createErrors.join(' ')} type="error" />
					</Form.Item>
				) : null}
				{updateErrors.length > 0 ? (
					<Form.Item>
						<Alert message={updateErrors.join(' ')} type="error" />
					</Form.Item>
				) : null}
				<Form.Item label="Name">
					{getFieldDecorator('name', {
						initialValue: formData.name,
						rules: [{ required: true, message: 'Please enter name of the part.' }],
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Is Active">
					{getFieldDecorator('isActive', {
						initialValue: formData.isActive === undefined ? true : formData.isActive,
						valuePropName: 'checked',
					})(<Switch />)}
				</Form.Item>
				<Form.Item label="Part Number">
					{getFieldDecorator('partNumber', {
						initialValue: formData.partNumber,
						rules: [{ required: true, message: 'Please enter part number.' }],
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Alias Part Number">
					{getFieldDecorator('aliasPartId', {
						initialValue: formData.aliasPartId,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				{isGLCodeAllowedBySettings(companyConfig) ? (
					<Form.Item label={'GL Code'}>
						{getFieldDecorator('glCode', {
							initialValue: formData.glCode,
						})(<Input style={{ maxWidth: 640 }} />)}
					</Form.Item>
				) : null}
				<Form.Item label="Description">
					{getFieldDecorator('description', {
						initialValue: formData.description,
					})(<Input.TextArea style={{ maxWidth: 640 }} />)}
				</Form.Item>
				{isUpdate ? null : (
					<>
						<Form.Item label="Vendors">
							{getFieldDecorator('partEquipmentVendorIds', {
								initialValue: formData.partEquipmentVendorIds,
							})(
								<OWAsyncSelect
									style={{ maxWidth: 640 }}
									mode="multiple"
									stateSlice={vendors}
									targetCollectionName="PART_VENDORS_AUTOCOMPLETE"
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleVendors(ids, targetCollectionName);
									}}
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) =>
										fetchVendors(
											{ name: searchText },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										)
									}
									placeholder={'Select Vendors'}
									createNewEntity={
										canCreateVendors
											? (name) => createVendor({ name, currencyId: 'USD' })
											: undefined
									}
									renderRecord={(vendor) => (
										<Select.Option key={vendor.id} value={vendor.id}>
											{vendor.name}
										</Select.Option>
									)}
								/>
							)}
						</Form.Item>
						<Form.Item label="Default Vendor">
							{getFieldDecorator('defaultVendorId', {
								initialValue: formData.defaultVendorId,
							})(
								<OWAsyncSelect
									style={{ maxWidth: 640 }}
									stateSlice={vendors}
									targetCollectionName="defaultPartEquipmentVendors"
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleVendors(ids, targetCollectionName);
									}}
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) =>
										fetchVendors(
											{ name: searchText },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										)
									}
									placeholder={'Select Default Vendor'}
									createNewEntity={
										canCreateVendors
											? (name) => createVendor({ name, currencyId: 'USD' })
											: undefined
									}
									renderRecord={(vendor) => (
										<Select.Option key={vendor.id} value={vendor.id}>
											{vendor.name}
										</Select.Option>
									)}
								/>
							)}
						</Form.Item>
					</>
				)}
				<Form.Item label="Manufacturer Name">
					{getFieldDecorator('manufacturerName', {
						initialValue: formData.manufacturerName,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Manufacturer Id">
					{getFieldDecorator('manufacturerId', {
						initialValue: formData.manufacturerId,
					})(<Input style={{ maxWidth: 640 }} />)}
				</Form.Item>
				<Form.Item label="Unit of Measure">
					{getFieldDecorator('uomId', {
						initialValue: formData.uomId,
						rules: [{ required: true, message: 'Please select unit of measure.' }],
					})(
						<Select style={{ maxWidth: 640 }}>
							{unitsOfMeasureRecords.map((uom) => (
								<Select.Option key={uom.id} value={uom.id}>
									{uom.displayNameSingular}
								</Select.Option>
							))}
						</Select>
					)}
				</Form.Item>
				{canShowCost ? (
					<Form.Item label="Cost">
						{getFieldDecorator('cost', {
							initialValue: formData.cost,
						})(
							<InputNumber
								style={{ minWidth: 120 }}
								min={0.0}
								formatter={(value) => `${value}`}
								precision={2}
								step={0.01}
								addonAfter={currency.id}
							/>
						)}
					</Form.Item>
				) : null}
				<Form.Item label="Photos">
					{getFieldDecorator(`images`, {
						valuePropName: 'fileList',
						getValueFromEvent: normFile,
						initialValue: this.state.allPhotos,
					})(
						<OWUpload
							accept="image/*"
							name="file"
							listType="picture-card"
							className="avatar-uploader"
							multiple={true}
							headers={uploadHeaders}
							onPreview={this.handlePreview}
							action={`${BACKEND_URI}/api/v1/supplier/file/upload`}
							beforeUpload={imageUploadValidation}
							onChange={this.handleImageUpdate}
						>
							<div style={{ display: 'inline' }}>
								<LegacyIcon type={this.state.uploadLoading ? 'loading' : 'plus'} />
								<div className="ant-upload-text">Upload</div>
							</div>
							<Modal visible={previewVisible} footer={null} onCancel={this.handleCancel}>
								<img alt="example" style={{ width: '100%' }} src={previewImage} />
							</Modal>
						</OWUpload>
					)}
				</Form.Item>
				<CustomFieldItems
					getFieldDecorator={getFieldDecorator}
					form={form}
					formData={formData}
					customFields={this.state.customFields}
				/>
				<Form.Item>
					<OWButton
						type="primary"
						htmlType="submit"
						loading={this.state.submitting || isImageUploading}
						className="contactForm__button"
					>
						{submitText}
					</OWButton>
					<span style={{ marginLeft: '16px' }}>
						{onCancel ? (
							<Button type="ghost" onClick={onCancel}>
								Cancel
							</Button>
						) : (
							<BackButton buttonText="Cancel" />
						)}
					</span>
				</Form.Item>
			</Form>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	formData: ownProps.formData,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	onSuccess: ownProps.onSuccess,

	createErrors: state.parts.createErrors,
	updateErrors: state.parts.updateErrors,
	creating: state.parts.creating,
	updating: state.parts.updating,
	unitsOfMeasure: state.units_of_measure,
	currentUser: state.session.currentUser,
	vendors: state.vendors,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	update: (entity) => dispatch(partsRestCrudThunksForSupplier.update(entity)),
	create: (entity) => dispatch(partsRestCrudThunksForSupplier.create(entity)),
	replaceVendors: (partId, entities) => dispatch(replacePartVendorsForSupplier(partId, entities)),
	fetchUnitsOfMeasure: (params) => dispatch(unitsOfMeasureRestCrudThunksForSupplier.read(params)),
	fetchVendors: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	getCustomFields: (params) => dispatch(getCustomFieldsForSupplier(params)),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	createVendor: (entity) => dispatch(vendorsRestCrudThunksForSupplier.create(entity)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Form.create<PartFormProps>()(PartForm))
);
